import {
  useIdentityToken,
  usePrivy,
  useToken,
  useWallets,
} from "@privy-io/react-auth";
import { useMasquerade } from "@/contexts/masquerade-context";

export const useAuth = () => {
  const {
    ready,
    authenticated,
    user,
    login,
    logout,
    linkEmail,
    linkWallet,
    unlinkEmail,
    unlinkWallet,
  } = usePrivy();

  const { wallets } = useWallets();

  // Get the primary wallet (first connected wallet)
  const primaryWallet = user?.linkedAccounts.find(
    (account) => account.type === "wallet"
  );

  const { identityToken } = useIdentityToken();
  const { getAccessToken } = useToken();
  const { impersonatedWallet } = useMasquerade();

  const realAddress = primaryWallet?.address;
  // While an admin is "viewing as" another wallet, the effective address is the
  // impersonated one so the whole app (queries, address display, leaderboard
  // highlight) follows the target. The backend independently honors the
  // matching X-Act-As header for the actual data.
  const address = impersonatedWallet ?? realAddress;
  const effectiveEmail = impersonatedWallet ? undefined : user?.email?.address;

  return {
    // User data
    user,
    email: effectiveEmail,
    address,
    realAddress,
    isImpersonating: Boolean(impersonatedWallet),
    userName: effectiveEmail || address || "Anonymous", // For backward compatibility

    // Authentication state
    isAuthenticated: authenticated,
    isSignedIn: authenticated,
    isLoading: !ready,

    // Wallet data
    primaryWallet,
    wallets,

    // Auth methods
    login,
    logout,
    linkEmail,
    linkWallet,
    unlinkEmail,
    unlinkWallet,
    openConnectModal: login, // For backward compatibility
    token: identityToken,
    hasApiToken: Boolean(identityToken),
    getAccessToken,
    getApiToken: async () => identityToken || await getAccessToken(),

    getSigner: async () => {
      // This would need to be implemented based on your specific needs
      // For now, return null or implement based on wallet provider
      return null;
    },

    signMessage: async (message: string) => {
      // This would need wallet-specific implementation
      // For now, return empty string or implement based on wallet provider
      return "";
    },
  };
};
